// Retkikohteet: valmiit kohteet, reitit ja kokoelmat sekä "Merkitse suoritetuksi"
// (suunnitelman kohdat 6, 14 ja 19).

import { useMemo, useState } from "preact/hooks";

import { day, dayFromIso, todayIso } from "../lib/format.js";
import { routeFacts, routeSourceNote } from "../lib/routeInfo.js";
import { routeTool } from "../lib/routeGeometry.js";
import { groupDistanceText, groupsByDistance, nearText } from "../lib/nearby.js";
import { EMPTY_FILTER, LENGTH_BANDS, filterActive, filterItems, municipalitiesOf } from "../lib/contentFilter.js";
import { DIFFICULTY_LABEL } from "../lib/routeInfo.js";
import { HiddenAdminPanel } from "./HiddenAdmin.jsx";
import { PoiCreateSheet } from "./PoiCreateSheet.jsx";
import { Sheet } from "./Sheet.jsx";

const VERIFICATION = {
  source_found: "Lähdelöytö",
  source_described: "Lähdekuvaus koottu",
  map_checked: "Karttatarkistettu",
  field_checked: "Maastotarkistettu",
  ready: "Valmis",
  temporarily_unavailable: "Tilapäisesti poissa",
};

const CATEGORY = { luonto: "Luonto", historia: "Historia", arkkitehtuuri: "Arkkitehtuuri", nakoala: "Näköala", taukopaikka: "Taukopaikka", muu: "Muu" };

function Mark({ done, route }) {
  return (
    <svg class="mark" viewBox="0 0 30 30" aria-hidden="true">
      {route ? (
        <path d="M4 22 C10 6, 20 26, 26 8" fill="none" stroke={done ? "var(--gold)" : "var(--rasti)"} stroke-width="3" stroke-dasharray={done ? "none" : "5 4"} stroke-linecap="round" />
      ) : (
        <>
          <circle cx="15" cy="15" r="10" fill="none" stroke={done ? "var(--gold)" : "var(--rasti)"} stroke-width="3" />
          <circle cx="15" cy="15" r="2.2" fill={done ? "var(--gold)" : "var(--rasti)"} />
        </>
      )}
    </svg>
  );
}

function StateLine({ state }) {
  if (!state.done) return <span>Ei vielä käyty</span>;
  return (
    <span class="state-done">
      {state.first_at ? `Käyty ${day(state.first_at)}` : "Käyty"}
      {state.confirmed_by_user && !state.auto ? ", oma vahvistus" : ""}
    </span>
  );
}

/** Yksi rivi kohde- tai reittilistassa. `meters` näytetään vain, jos kohteelle on asetettu tarkka sijainti. */
function ItemRow({ app, tab, it, meters }) {
  return (
    // Reitti ja kohde avautuvat kartalla (näkyviin ja tarkennus). Ilman linjaa tai sijaintia avautuu kortti.
    <button class="item" onClick={() => (tab === "routes" ? app.showRoute(it.id) : app.showPoi(it.id))}>
      <Mark done={it.state.done} route={tab === "routes"} />
      <div>
        <div class="t">{it.name}</div>
        {meters != null && <div class="s"><b class="dist">{nearText(meters)}</b></div>}
        {tab === "routes" && routeFacts(it) && <div class="s">{routeFacts(it)}</div>}
        {tab === "pois" && !it.has_location && <div class="s no-point">Piste puuttuu. Aseta se paikan päällä.</div>}
        <div class="s"><StateLine state={it.state} /></div>
      </div>
      <div class="end"><b>{it.points}</b><br />p</div>
    </button>
  );
}

/**
 * Kohteet tai reitit alueittain, lähin alue ensin (kun sijainti on tiedossa).
 * Järjestys on karkea: alueen keskipisteestä, ellei kohteelle ole asetettu omaa pistettä.
 * Ilman sijaintia lista on alkuperäisessä järjestyksessä.
 */
function ItemList({ app, tab, list }) {
  const me = app.me;
  const kind = tab === "pois" ? "poi" : "route";
  // Sijainti pyöristetään (~1 km), ettei lista hypi jokaisesta GPS-pisteestä; karkea järjestys ei tarvitse tarkempaa.
  const key = me ? `${me.latitude.toFixed(2)},${me.longitude.toFixed(2)}` : "";
  const groups = useMemo(() => groupsByDistance(kind, list, me), [list, kind, key]);

  return (
    <>
      {!me && (
        <p class="notice" role="status">
          Sijaintiasi ei vielä tiedetä. Kun se on tiedossa, lähimmät alueet tulevat listan alkuun.{" "}
          <button class="btn btn-quiet" onClick={() => app.locateOnce()}>Hae sijaintini</button>
        </p>
      )}
      {groups.map((g) => {
        const away = groupDistanceText(g);
        return (
          <section key={g.label}>
            <div class="area-head">
              <h2>{g.label}</h2>
              <span>{away && <>{away} · </>}{g.items.filter((x) => x.item.state.done).length}/{g.items.length}</span>
            </div>
            {g.items.map(({ item, meters }) => <ItemRow key={item.id} app={app} tab={tab} it={item} meters={meters} />)}
          </section>
        );
      })}
    </>
  );
}

/** Pelaajan löytämät salaiset kohteet. Näkyy vasta ensimmäisen löydön jälkeen, joten se ei paljasta, että niitä on. */
function FoundSecrets({ app, list }) {
  if (!list?.length) return null;
  return (
    <section>
      <div class="area-head"><h2>Salaiset löydöt</h2><span>{list.length}</span></div>
      {list.map((h) => (
        <button class="item" key={h.id} onClick={() => app.openContent("hidden", h.id)}>
          <svg class="mark" viewBox="0 0 30 30" aria-hidden="true">
            <path d="M15 4l3.4 7.2 7.8 1-5.7 5.4 1.5 7.7L15 21.4 8 25.3l1.5-7.7-5.7-5.4 7.8-1z" fill="var(--gold)" stroke="var(--gold)" stroke-width="1.5" stroke-linejoin="round" />
          </svg>
          <div>
            <div class="t">{h.name}</div>
            <div class="s state-done">Löydetty {h.found_at ? day(h.found_at) : ""}</div>
          </div>
          <div class="end"><b>{h.points}</b><br />p</div>
        </button>
      ))}
    </section>
  );
}

// Valittu välilehti ja suodattimet säilyvät, kun näkymä poistuu ja palaa (esim. reitti avataan kartalla ja palataan listaan).
let rememberedTab = "pois";
const rememberedFilters = { pois: { ...EMPTY_FILTER }, routes: { ...EMPTY_FILTER } };

/** Haku ja suodattimet kohde- tai reittilistan yläpuolella. Suodattimet ovat valintoja, ei kirjoitettavaa (haku lukuun ottamatta). */
function FilterBar({ kind, list, filter, onChange, shown }) {
  const set = (patch) => onChange({ ...filter, ...patch });
  const active = filterActive(filter);
  const select = (id, label, key, options) => (
    <div class="filter-field">
      <label for={id}>{label}</label>
      <select id={id} value={filter[key]} onChange={(e) => set({ [key]: e.currentTarget.value })}>
        <option value="">Kaikki</option>
        {options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
      </select>
    </div>
  );
  return (
    <div class="filter-bar">
      <label class="sr-only" for="content-search">Hae nimellä, alueelta tai kunnasta</label>
      <input
        id="content-search" type="search" value={filter.query} placeholder={kind === "routes" ? "Hae reittiä, esim. kuntorata" : "Hae kohdetta"}
        autoComplete="off" onInput={(e) => set({ query: e.currentTarget.value })}
      />
      <details class="filter-more" open={active && Object.entries(filter).some(([k, v]) => k !== "query" && v)}>
        <summary>Suodattimet{active ? " (käytössä)" : ""}</summary>
        <div class="filter-grid">
          {select("f-muni", "Kunta", "municipality", municipalitiesOf(list).map((m) => [m, m]))}
          {select("f-status", "Käyntitila", "status", [["todo", "Ei vielä käyty"], ["done", "Käyty"]])}
          {kind === "routes" && select("f-length", "Pituus", "length", LENGTH_BANDS.map((b) => [b.id, b.label]))}
          {kind === "routes" && select("f-diff", "Vaativuus", "difficulty", Object.entries(DIFFICULTY_LABEL))}
          {select("f-line", kind === "routes" ? "Reittilinja" : "Tarkka piste", "line", kind === "routes" ? [["yes", "Linja kartalla"], ["no", "Linja puuttuu"]] : [["yes", "Piste asetettu"], ["no", "Piste puuttuu"]])}
        </div>
      </details>
      <p class="s num" aria-live="polite">
        {shown} / {list.length} {kind === "routes" ? "reittiä" : "kohdetta"}
        {active && <> · <button class="link-like" onClick={() => onChange({ ...EMPTY_FILTER })}>Tyhjennä haku ja suodattimet</button></>}
      </p>
    </div>
  );
}

export function ContentView({ app }) {
  const [chosenTab, setChosenTab] = useState(rememberedTab);
  const [creating, setCreating] = useState(false); // ylläpidon "Lisää kohde tähän" -arkki
  const [, setFilterTick] = useState(0);
  const setTab = (id) => {
    rememberedTab = id;
    setChosenTab(id);
  };
  const { content } = app;
  // Ylläpitovälilehti katoaa, kun ylläpito poistetaan käytöstä.
  const tab = chosenTab === "secrets" && !app.admin ? "pois" : chosenTab;

  if (!content) {
    return (
      <div class="page"><div class="page-inner">
        <h1>Retkikohteet</h1>
        <div class="empty"><b>Kohteita ei saatu ladattua</b>{app.loadError || "Tarkista palvelimen osoite Passin asetuksista."}</div>
      </div></div>
    );
  }

  const kind = tab === "pois" ? "pois" : "routes";
  const fullList = tab === "pois" ? content.pois : content.routes;
  const filter = rememberedFilters[kind];
  const list = filterItems(fullList, kind, filter);
  const doneCount = list.filter((x) => x.state.done).length;
  const changeFilter = (next) => {
    rememberedFilters[kind] = next;
    setFilterTick((n) => n + 1); // muistettu suodatin on moduulin tilassa: päivitetään näkymä
  };

  return (
    <div class="page">
      <div class="page-inner">
        <h1>Retkikohteet</h1>
        <p class="lede">Valmiit kohteet ja reitit Lappeenrannan seudulla. Käynti tunnistuu kartalta, tai voit merkitä sen itse.</p>
        <div class={`seg ${app.admin ? "seg-many" : ""}`} role="group" aria-label="Näytä">
          <button aria-pressed={tab === "pois"} onClick={() => setTab("pois")}>Kohteet</button>
          <button aria-pressed={tab === "routes"} onClick={() => setTab("routes")}>Reitit</button>
          <button aria-pressed={tab === "collections"} onClick={() => setTab("collections")}>Kokoelmat</button>
          <button aria-pressed={tab === "wishes"} onClick={() => setTab("wishes")}>Toiveet</button>
          {app.admin && <button aria-pressed={tab === "secrets"} onClick={() => setTab("secrets")}>Salaiset</button>}
        </div>

        {tab === "secrets" && app.admin ? (
          <HiddenAdminPanel app={app} />
        ) : tab === "wishes" ? (
          <WishlistsPanel app={app} />
        ) : tab === "collections" ? (
          content.collections.map((c) => (
            <div class="coll" key={c.id}>
              <div class="area-head">
                <h2>{c.name}</h2>
                <span class={c.state.done ? "state-done" : ""}>{c.done_count}/{c.members.length}</span>
              </div>
              <div class="s">{c.description}</div>
              <div class="members">
                {c.members.map((m) => (
                  <span key={m.key} class={m.done ? "done" : ""}>{m.done ? "✓ " : ""}{m.name}</span>
                ))}
              </div>
            </div>
          ))
        ) : (
          <>
            <FilterBar kind={kind} list={fullList} filter={filter} onChange={changeFilter} shown={list.length} />
            <p class="s num" aria-live="polite">{doneCount} / {list.length} käyty</p>
            {tab === "pois" && app.admin && (
              <button class="btn btn-block" onClick={() => setCreating(true)}>Lisää kohde tähän (GPS)</button>
            )}
            {tab === "pois" && <FoundSecrets app={app} list={content.hidden} />}
            {list.length === 0 ? (
              <div class="empty">
                <b>Ei hakua vastaavia {tab === "pois" ? "kohteita" : "reittejä"}</b>
                Kokeile toista hakusanaa tai tyhjennä suodattimet.
              </div>
            ) : (
              <ItemList app={app} tab={tab} list={list} />
            )}
          </>
        )}
      </div>
      {creating && <PoiCreateSheet app={app} onClose={() => setCreating(false)} />}
    </div>
  );
}

/** Löydetyn salaisen kohteen kortti: nimi, kuvaus ja paikka kartalla. */
function HiddenFoundSheet({ app, id, onClose }) {
  const item = app.content?.hidden?.find((h) => h.id === id);
  if (!item) return null;
  return (
    <Sheet title={item.name} onClose={onClose}>
      <p class="hint">Salainen kohde{item.found_at ? `, löydetty ${day(item.found_at)}` : ""}</p>
      {item.description && <p>{item.description}</p>}
      <p class="s"><b>Löytöpisteet {item.points}</b></p>
      <div class="sheet-actions">
        <button class="btn btn-primary" onClick={() => { onClose(); app.showOnMap([[item.lat - 0.002, item.lon - 0.003], [item.lat + 0.002, item.lon + 0.003]]); }}>Näytä kartalla</button>
        <button class="btn" onClick={onClose}>Sulje</button>
      </div>
    </Sheet>
  );
}

/** Kohteen tai reitin kortti tai löydetyn salaisen kohteen kortti. */
export function ContentSheet(props) {
  return props.kind === "hidden" ? <HiddenFoundSheet {...props} /> : <ContentItemSheet {...props} />;
}

/** Kohteen tai reitin kortti ja oma vahvistus. */
function ContentItemSheet({ app, kind, id, onClose }) {
  const item = useMemo(() => (kind === "poi" ? app.content?.pois : app.content?.routes)?.find((x) => x.id === id), [app.content, kind, id]);
  const [confirming, setConfirming] = useState(false);
  const [doneOn, setDoneOn] = useState(todayIso());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [wishOpen, setWishOpen] = useState(false);

  if (!item) return null;
  const s = item.state;
  const located = kind === "poi" ? item.has_location : item.has_geometry;
  // Pisteen/reitin asetus kartalla. Pisteettömällä kohteella kartta aukeaa omaan sijaintiin, ja
  // paneelin "Käytä sijaintiani" siirtää tähtäimen paikalle.
  const startPlacing = () =>
    app.startTool(
      kind === "poi"
        ? { type: "poi", id: item.id, name: item.name, radius: item.visit_radius_m, start: located ? [item.lat, item.lon] : null }
        : { type: "route", id: item.id, name: item.name, ...routeTool(item.geometry) },
    );

  const submit = async () => {
    setBusy(true);
    const ok = await app.confirm({ kind, target: item.id, done_on: doneOn, note: note || null });
    setBusy(false);
    if (ok) setConfirming(false);
  };

  return (
    <Sheet title={item.name} onClose={onClose}>
      <p class="hint">
        {item.area === item.municipality ? item.area : `${item.area}, ${item.municipality}`}
        {kind === "poi" && CATEGORY[item.category] ? `, ${CATEGORY[item.category].toLowerCase()}` : ""}
        {kind === "route" && routeFacts(item) ? `, ${routeFacts(item)}` : ""}
      </p>
      {kind === "route" && routeSourceNote(item) && <p class="hint">{routeSourceNote(item)} Pisteet määräytyvät pituudesta ja vaativuudesta.</p>}
      <p>{item.description}</p>
      {item.note && <div class="notice warn">{item.note}</div>}

      {kind === "route" && item.progress_estimate != null && !s.done && (
        <div>
          <div class="s">Kuljettu arviolta {Math.round(item.progress_estimate * 100)} %</div>
          <div class="meter"><i style={{ width: `${Math.round(item.progress_estimate * 100)}%` }} /></div>
        </div>
      )}

      {!located && (
        <div class="notice">
          {kind === "poi"
            ? "Kohteelle ei ole vielä pistettä, joten käyntiä ei tunnisteta automaattisesti. Kun olet paikalla, paina \"Aseta piste\" ja sitten \"Käytä sijaintiani\". Oma merkintä antaa saman palkinnon."
            : "Reittilinja odottaa karttatarkistusta, joten käyntiä ei vielä tunnisteta automaattisesti. Oma merkintä antaa saman palkinnon."}
          {kind === "poi" && app.admin && (
            <div style={{ marginTop: 8 }}>
              <button class="btn btn-block" onClick={startPlacing}>Aseta piste</button>
            </div>
          )}
        </div>
      )}

      <p>
        <b>{s.done ? "Suoritettu" : "Ei vielä suoritettu"}</b>
        {s.done && s.first_at ? ` ${day(s.first_at)}` : ""}
        {s.auto && <span class="badge" style={{ marginLeft: 8 }}>tunnistettu retkeltä</span>}
        {s.confirmed_by_user && <span class="badge self" style={{ marginLeft: 8 }}>oma vahvistus</span>}
        <br />
        <span class="s">Ensisuoritus {item.points} pistettä</span>
        <br />
        <span class="s">Tarkistustila: {VERIFICATION[item.verification] ?? item.verification}</span>
      </p>

      {confirming ? (
        <div>
          <div class="field">
            <label for="done-on">Suorituspäivä</label>
            <input id="done-on" type="date" value={doneOn} max={todayIso()} onInput={(e) => setDoneOn(e.currentTarget.value)} />
          </div>
          <div class="field">
            <label for="note">Huomautus, vapaaehtoinen</label>
            <textarea id="note" value={note} maxLength={1000} placeholder="Esimerkiksi: kiersin suljetun osuuden" onInput={(e) => setNote(e.currentTarget.value)} />
          </div>
          <div class="sheet-actions">
            <button class="btn btn-rasti btn-block" disabled={busy || !doneOn} onClick={submit}>
              Merkitse suoritetuksi {dayFromIso(doneOn)}
            </button>
            <button class="btn btn-block" onClick={() => setConfirming(false)}>Peruuta</button>
          </div>
        </div>
      ) : (
        <div class="sheet-actions">
          {!s.confirmed_by_user && (
            <button class="btn btn-rasti btn-block" onClick={() => setConfirming(true)}>Merkitse suoritetuksi</button>
          )}
          {s.confirmed_by_user && (
            <button
              class="btn btn-danger btn-block"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                await app.revoke(s.confirmation_id);
                setBusy(false);
              }}
            >
              Peru oma vahvistus
            </button>
          )}
          {s.confirmed_by_user && s.auto && <p class="s">Retkeltä tunnistettu käynti pysyy voimassa, vaikka perut oman merkinnän.</p>}
        </div>
      )}

      <div class="secondary-actions">
        <button class="btn" aria-expanded={wishOpen} onClick={() => setWishOpen(!wishOpen)}>
          {inWishlists(app.wishlists, `${kind}:${item.id}`) ? "Toivelistoilla" : "Toivelistalle"}
        </button>
        {app.admin && (
        <button class="btn" onClick={startPlacing}>
          {kind === "poi" ? (located ? "Siirrä pistettä" : "Aseta piste") : located ? "Muokkaa reittiä" : "Piirrä reitti"}
        </button>
        )}
      </div>
      {wishOpen && <WishChooser app={app} itemKey={`${kind}:${item.id}`} />}

      {item.sources.length > 0 && (
        <p class="s">
          Lähteet:{" "}
          {item.sources.map((src, i) => (
            <span key={src.id}>
              {i > 0 && ", "}
              {src.url ? <a href={src.url} target="_blank" rel="noopener noreferrer">{src.id}</a> : src.id}
              {src.attribution && ` (${src.attribution})`}
            </span>
          ))}
        </p>
      )}
    </Sheet>
  );
}

// --- Toivelistat ----------------------------------------------------------------

const inWishlists = (lists, key) => lists?.some((l) => l.items.some((i) => i.key === key));

/** Kohteen lisäys toivelistoille: valintaruudut ja uuden listan luonti. */
function WishChooser({ app, itemKey }) {
  const [name, setName] = useState("");
  const lists = app.wishlists ?? [];

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const id = await app.createWishlist(name.trim());
    if (id) {
      await app.toggleWish(id, itemKey, false);
      setName("");
    }
  };

  return (
    <div class="wish-chooser">
      {lists.map((l) => {
        const present = l.items.some((i) => i.key === itemKey);
        return (
          <label class="check" key={l.id}>
            <input type="checkbox" checked={present} onChange={() => app.toggleWish(l.id, itemKey, present)} />
            <span>{l.name}</span>
          </label>
        );
      })}
      <form class="inline-form" onSubmit={create}>
        <label class="sr-only" for="new-list">Uuden listan nimi</label>
        <input id="new-list" value={name} maxLength={80} placeholder={lists.length ? "Uusi lista" : "Esimerkiksi: Lapsen kanssa"} onInput={(e) => setName(e.currentTarget.value)} />
        <button class="btn" type="submit" disabled={!name.trim()}>Luo ja lisää</button>
      </form>
    </div>
  );
}

function WishlistsPanel({ app }) {
  const [editing, setEditing] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(null); // poistettavaksi vahvistettava lista
  const lists = app.wishlists ?? [];

  if (!lists.length) {
    return (
      <div class="empty">
        <b>Ei vielä toivelistoja</b>
        Avaa kohde tai reitti ja valitse Toivelistalle. Listoja voi nimetä vapaasti, esimerkiksi Pyöräretket tai Mökin lähikohteet.
      </div>
    );
  }

  return lists.map((l) => (
    <section key={l.id}>
      <div class="area-head">
        <h2>{l.name}</h2>
        <span>{l.done_count}/{l.items.length} käyty</span>
      </div>
      {l.items.length === 0 && <p class="s">Lista on tyhjä.</p>}
      {l.items.map((i) => {
        const [kind, id] = i.key.split(":");
        return (
          <div class="item wish-item" key={i.key}>
            <Mark done={i.done} route={kind === "route"} />
            <button class="link-like" onClick={() => app.openContent(kind, id)}>
              <span class="t">{i.name}</span>
              <span class="s">{i.done ? "Käyty, säilyy muistona" : "Odottaa käyntiä"}</span>
            </button>
            <button class="btn btn-quiet" aria-label={`Poista ${i.name} listalta ${l.name}`} onClick={() => app.toggleWish(l.id, i.key, true)}>Poista</button>
          </div>
        );
      })}
      {editing?.id === l.id ? (
        <form class="inline-form" onSubmit={async (e) => {
          e.preventDefault();
          try {
            await app.api.renameWishlist(l.id, editing.name);
            setEditing(null);
            app.refresh();
          } catch (err) {
            app.toast({ kind: "info", what: "Nimeä ei tallennettu", sub: err.message });
          }
        }}>
          <label class="sr-only" for={`rename-${l.id}`}>Listan nimi</label>
          <input id={`rename-${l.id}`} value={editing.name} maxLength={80} onInput={(e) => setEditing({ ...editing, name: e.currentTarget.value })} />
          <button class="btn" type="submit" disabled={!editing.name.trim()}>Tallenna</button>
        </form>
      ) : (
        <div class="list-tools">
          <button class="btn btn-quiet" onClick={() => setEditing({ id: l.id, name: l.name })}>Nimeä</button>
          {deleting === l.id ? (
            <>
              <span class="s">Lista poistuu, kohteet ja suoritukset säilyvät.</span>
              <button class="btn btn-danger" onClick={async () => {
                await app.api.deleteWishlist(l.id);
                setDeleting(null);
                app.refresh();
              }}>Poista lista</button>
              <button class="btn btn-quiet" onClick={() => setDeleting(null)}>Säilytä</button>
            </>
          ) : (
            <button class="btn btn-quiet danger-text" onClick={() => setDeleting(l.id)}>Poista lista</button>
          )}
        </div>
      )}
    </section>
  ));
}

